import { TableKeys, ColumnsConfig, DEFAULT_COLUMN_KEY } from '@/config/tableColumns';
import store from '@/store';
import { cloneDeep } from 'lodash';
import { ref } from 'vue';
import i18n from '@/i18n';

const COLUM_SETTING_KEYS = 'TableColumnSettings';

const $t = (text) => {
  return i18n.global.t(text);
};

export function useColumnSetting(tableKey, projectId = '', customKeys = {}, key = 'value') {
  const activeColumns = ref([]);
  // const baseMetric = ref([]);
  const specifier = ref(DEFAULT_COLUMN_KEY);
  const customizeData = ref({});
  const translatePrefixKey = ref('');
  const ignoreReTranslateKeys = ref([]);
  const customCell = ref(customKeys);

  const project = projectId ?? store.state.project.value;
  // const extractKey = computed(() => (specifier.value ? specifier.value : DEFAULT_COLUMN_KEY));

  const loadDefaultColumns = () => {
    if (!Object.values(TableKeys).includes(tableKey) || !Object.hasOwn(ColumnsConfig, tableKey))
      return [];

    const columns = cloneDeep(ColumnsConfig);
    // baseMetric.value = columns[tableKey][DEFAULT_METRIC_KEY] ?? []
    return columns[tableKey][DEFAULT_COLUMN_KEY] ?? [];
  };

  const getColumns = () => {
    let columns;
    if (!isStored()) {
      columns = loadDefaultColumns();
    } else {
      const storedColumns = getStoredColumns();
      columns = storedColumns[project][tableKey][specifier.value] ?? [];
    }

    columns = translateText(columns);

    return columns.length ? columns.sort((a, b) => a.order - b.order) : [];
  };

  const translateText = (columns = []) => {
    return columns.map((column) => {
      if (ignoreReTranslateKeys.value.includes(column.id)) return column;

      let localeKey = column.id;
      if (customizeData.value[column.id] && customizeData.value[column.id]['text']) {
        localeKey = customizeData.value[column.id]['text'];
      }

      const translateKey = translatePrefixKey.value
        ? translatePrefixKey.value + '.' + localeKey
        : localeKey;

      return { ...column, text: $t(translateKey) };
    });
  };

  const getActiveColumns = () => {
    const columns = getColumns();

    return columns.filter((column) => column.isDefault || column.isVisible);
  };

  /**
   * @param newSpecifier
   *
   * call this function with specifier when columns of the table is depends on specifier dynamically
   * E.g. in Performance List, when change Product type, the metrics will be change
   */
  const setTableSpecifier = (newSpecifier) => {
    specifier.value = newSpecifier || DEFAULT_COLUMN_KEY;
  };

  /**
   * MUST call this function in unmounted hook to reset specifier or setTableSpecifier,
   * If NOT, other function's result will be impacted
   */
  const removeTableSpecifier = () => (specifier.value = '');

  /**
   * MUST set specifier first, customizeData will apply for specific specifier
   *
   *   customizeData is support for cases need customize like translate metrics to regIndi in logCollection
   *   E.g. customizeData =
   *   {
   *     createdByName: {text: "regIndi", value: "metricName"},
   *     id2: {text: "regIndi", value: "metricName"},
   *   }
   *
   * @param newData
   * @param translateKey
   * @param ignoreTranslateKeys
   */
  const setCustomizeData = (newData = {}, translateKey = '', ignoreTranslateKeys = []) => {
    customizeData.value = newData;
    translatePrefixKey.value = translateKey;
    ignoreReTranslateKeys.value = ignoreTranslateKeys;
  };

  const setCustomCell = (newCustomCell = {}) => {
    customCell.value = newCustomCell;
    activeColumnsWithCustom();
  };

  const updateCustomCell = (newCustomCell = {}) => {
    customCell.value = { ...customCell.value, ...newCustomCell };
    activeColumnsWithCustom();
  };

  const resetCustomCell = () => setCustomCell({});

  /**
   * === Example of using with custom fields ===
   * const columns = activeColumnsWithCustom('log_metrics_table_key', 'project_id_1', {
   *   value_of_key_1: { customCell: 'customCell1' },
   *   value_of_key_2: { customCell: 'customCell1', customCellX: 'customCell2' }
   * });
   *
   *  // value_of_key_1 is the value of column[param key]
   *  // customCell1 can be an object or a function, so on
   * === End Example ===
   */
  const activeColumnsWithCustom = () => {
    const columns = getColumns();
    let customColumns = ref([]);

    customColumns.value = columns.map((column) => {
      for (const [valueKey, customItems] of Object.entries(customCell.value)) {
        if (valueKey === column[key]) {
          return { ...column, ...customItems, isNotheaderResize: column.isDefault };
        }
      }

      return column;
    });

    const filteredColumns = customColumns.value.filter(
      (column) => column.isDefault || column.isVisible
    );

    activeColumns.value = filteredColumns;

    return filteredColumns;
  };

  const updateColumns = (newColumns, defaultColumnKeys = [], visibleColumnKeys = []) => {
    let storedColumns = getStoredColumns();

    if (defaultColumnKeys.length) newColumns = markColumnsDefault(newColumns, defaultColumnKeys);
    if (visibleColumnKeys.length) newColumns = markColumnsVisible(newColumns, visibleColumnKeys);

    newColumns = mergeNewColumnsWithBase(newColumns);

    if (!storedColumns) {
      storedColumns = {
        [project]: {
          [tableKey]: {
            [specifier.value]: newColumns
          }
        }
      };
    } else {
      if (!storedColumns[project]) {
        storedColumns = {
          ...storedColumns,
          [project]: {
            [tableKey]: {
              [specifier.value]: newColumns
            }
          }
        };
      } else {
        if (!storedColumns[project][tableKey]) {
          storedColumns = {
            [project]: {
              ...storedColumns[project],
              [tableKey]: {
                [specifier.value]: newColumns
              }
            }
          };
        } else {
          storedColumns[project][tableKey][specifier.value] = newColumns;
        }
      }
    }

    localStorage.setItem(COLUM_SETTING_KEYS, JSON.stringify(storedColumns));

    activeColumnsWithCustom();
  };

  const mergeNewColumnsWithBase = (newColumns = []) => {
    const baseColumns = loadDefaultColumns();
    if (!baseColumns.length) return newColumns;
    let columns = [];
    
    newColumns.forEach((newColumn) => {
      const index = baseColumns.findIndex((c) => c.id === newColumn.id);
      if (index > -1) {
        columns.push({ ...baseColumns[index], ...newColumn });
      } else {
        columns.push(newColumn);
      }
    });

    return columns;
  };

  const markColumnsDefault = (columns = [], defaultColumns = []) => {
    return columns.map((column) => {
      return {
        ...column,
        isDefault: defaultColumns.includes(column.id)
      };
    });
  };

  const markColumnsVisible = (columns = [], visibleColumns = []) => {
    return columns.map((column) => {
      return {
        ...column,
        isVisible: visibleColumns.includes(column.id)
      };
    });
  };

  const transformIdsToColumnFormat = (ids = []) => {
    // Add more if needed as struct of return column
    const columnKeys = ['text', 'value', 'order', 'isVisible', 'sortable', 'width'];
    const customizeKeys = Object.keys(customizeData.value);

    // todo: handle order value if have customize order
    let order = 1;
    return ids.map((id) => {
      let customValue = {};
      if (customizeKeys.includes(id)) {
        columnKeys.forEach((columnKey) => {
          if (customizeData.value[id][columnKey]) {
            customValue[columnKey] = customizeData.value[id][columnKey];
          }
        });
      }

      let localeKey = customValue['text'] ? customValue['text'] : id;
      const translateKey = translatePrefixKey.value
        ? translatePrefixKey.value + '.' + localeKey
        : localeKey;

      let column = {
        id: id,
        text: $t(translateKey),
        value: customValue['value'] ?? id,
        order: customValue['order'] ?? order++
      };

      if (Object.hasOwn(customValue, 'isVisible')) column['isVisible'] = customValue['isVisible'];
      if (Object.hasOwn(customValue, 'sortable')) column['sortable'] = customValue['sortable'];
      if (Object.hasOwn(customValue, 'width')) column['width'] = customValue['width'];

      return column;
    });
  };

  const resetOrder = (columns = [], fromOrder = 1) => {
    let order = fromOrder;
    return columns.map((column) => {
      return { ...column, order: order++ };
    });
  };

  const reset = () => {
    if (!isStored()) return;

    const storedColumns = getStoredColumns();

    if (specifier.value === DEFAULT_COLUMN_KEY) {
      delete storedColumns[project][tableKey];
    } else {
      delete storedColumns[project][tableKey][specifier.value];
    }

    localStorage.setItem(COLUM_SETTING_KEYS, JSON.stringify(storedColumns));
  };

  const resetAll = () => {
    const storedColumns = getStoredColumns();

    if (storedColumns && storedColumns[project]) delete storedColumns[project];

    localStorage.setItem(COLUM_SETTING_KEYS, JSON.stringify(storedColumns));
  };

  const clearAllColumnsSetting = () => {
    if (localStorage.getItem(COLUM_SETTING_KEYS)) localStorage.removeItem(COLUM_SETTING_KEYS);
  };

  const getStoredColumns = () => {
    return JSON.parse(localStorage.getItem(COLUM_SETTING_KEYS));
  };

  const isStored = () => {
    const storedColumns = getStoredColumns();

    return !!(
      storedColumns &&
      storedColumns[project] &&
      storedColumns[project][tableKey] &&
      storedColumns[project][tableKey][specifier.value]
    );
  };

  const needToBeRefresh = (returnList = [], defaultList = []) => {
    const columns = getColumns();

    if (!columns.length || columns.length !== returnList.length) return true;

    let needRefresh = false;
    columns.forEach((column) => {
      const checkExistFail = !returnList.includes(column.id);
      const checkDefaultFail =
        (defaultList.includes(column.id) && !column.isDefault) ||
        (!defaultList.includes(column.id) && column.isDefault);

      if (checkExistFail || checkDefaultFail) needRefresh = true;
    });

    return needRefresh;
  };

  return {
    activeColumns,
    getColumns,
    getActiveColumns,
    activeColumnsWithCustom,
    updateColumns,
    markColumnsDefault,
    reset,
    resetAll,
    clearAllColumnsSetting,
    getStoredColumns,
    setTableSpecifier,
    removeTableSpecifier,
    isStored,
    setCustomizeData,
    setCustomCell,
    updateCustomCell,
    resetCustomCell,
    transformIdsToColumnFormat,
    needToBeRefresh,
    resetOrder
  };
}
